import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

export type UseOnVisibleOptions = {
  /** Run callback only when the element is fully in view (threshold 1). Default: false (any part visible). */
  fullyVisible?: boolean;
  /** Delay in ms before observing, to avoid firing when element is briefly visible on mount. Default: 0. */
  initialDelay?: number;
  /** If true, run callback at most once and then stop observing. Default: false. */
  once?: boolean;
};

/**
 * Hook that runs a callback when an element becomes visible in the viewport.
 * Uses IntersectionObserver; no tracking or analytics.
 * Accepts the element ref from the caller so the same ref can be shared (e.g. with useVisibilityTracker).
 *
 * @param elementRef - Ref for the element to observe (attach this to the DOM node)
 * @param onVisible - Callback run when the element intersects the viewport
 * @param options - Optional: fullyVisible, initialDelay, once
 */
function useOnVisible<T extends HTMLElement = HTMLElement>(
  elementRef: MutableRefObject<T | null>,
  onVisible: () => void,
  options: UseOnVisibleOptions = {}
): void {
  const { fullyVisible = false, initialDelay = 0, once = false } = options;
  const timeoutRef = useRef<number>();
  const onVisibleRef = useRef(onVisible);
  const observedRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  onVisibleRef.current = onVisible;

  const handleIntersect = useCallback<(entries: IntersectionObserverEntry[]) => void>(
    (entries) => {
      const entry = entries[0];
      if (!entry?.isIntersecting) return;
      if (once && observedRef.current) return;

      observedRef.current = true;
      onVisibleRef.current();

      if (once && observerRef.current && elementRef.current) {
        observerRef.current.unobserve(elementRef.current);
      }
    },
    // elementRef omitted: ref identity is stable and we only read .current; including it would recreate the observer every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [once]
  );

  const observer = useMemo(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return null;
    }
    return new IntersectionObserver(handleIntersect, {
      threshold: fullyVisible ? 1 : 0,
    });
  }, [handleIntersect, fullyVisible]);

  observerRef.current = observer;

  useEffect(() => {
    const element = elementRef?.current ?? null;

    const startObserving = () => {
      if (element && observer) {
        observer.observe(element);
      }
    };

    if (initialDelay > 0) {
      timeoutRef.current = window.setTimeout(startObserving, initialDelay);
    } else {
      startObserving();
    }

    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      if (observer) {
        if (element && typeof observer.unobserve === 'function') {
          observer.unobserve(element);
        }
        observer.disconnect();
      }
    };
  }, [observer, initialDelay, elementRef]);
}

export default useOnVisible;
